/**
 * Activity Log → Events drill-down fixture — task 177.
 *
 * Provides a small, deterministic event list for the
 * `useActivityLogEvents` hook in mockup mode. Roughly half of the rows
 * carry a `screenshotId` (so the new thumbnail column lights up); the
 * other half carry `screenshotId: null` (so the page also exercises the
 * blank-cell branch).
 *
 * Dates derive from FROZEN_NOW per DESIGN-RULES.md Rule 21. Slug ids
 * align with the established cross-reference convention used by
 * EMPLOYEES, CLIENTS, PROJECTS (see `employees.ts`, `clients.ts`,
 * `projects.ts`).
 *
 * The screenshotIds in this fixture (`scr-demo-001`, etc.) are stable
 * strings the page hands to `<ScreenshotImage>`; in mockup mode
 * `useScreenshotUrl` returns a 1×1 transparent PNG data URL regardless
 * of the id, so the surface renders without contacting any image
 * backend.
 */

import type { ActivityLogEventsResponse } from '@/types/reports';
import { FROZEN_NOW_MS } from '@/lib/frozenTime';

const HOUR_MS = 60 * 60 * 1000;
const isoOffsetHours = (hoursAgo: number): string =>
  new Date(FROZEN_NOW_MS - hoursAgo * HOUR_MS).toISOString();

const ALPHA = {
  id: 'prj-meridian-capital-redesign',
  name: 'Brand Refresh',
  clientId: 'cli-meridian-capital',
  clientName: 'Meridian Capital',
} as const;

export const ACTIVITY_LOG_EVENTS_RESPONSE: ActivityLogEventsResponse = {
  org: { id: 'org-meridian', name: 'Meridian Associates', timezone: 'America/New_York' },
  startDate: new Date(FROZEN_NOW_MS - 30 * 24 * HOUR_MS).toISOString(),
  endDate: new Date(FROZEN_NOW_MS).toISOString(),
  filter: { app: 'Figma', title: 'Brand Refresh — Hero' },
  events: [
    {
      id: 'evt-act-001',
      occurredAt: isoOffsetHours(2),
      app: 'Figma',
      title: 'Brand Refresh — Hero',
      site: 'figma.com',
      url: null,
      screenshotId: 'scr-demo-001',
      employee: { id: 'emp-sarah-chen', name: 'Sarah Chen' },
      project: ALPHA,
    },
    {
      id: 'evt-act-002',
      occurredAt: isoOffsetHours(3),
      app: 'Figma',
      title: 'Brand Refresh — Hero',
      site: 'figma.com',
      url: null,
      screenshotId: null,
      employee: { id: 'emp-sarah-chen', name: 'Sarah Chen' },
      project: ALPHA,
    },
    {
      id: 'evt-act-003',
      occurredAt: isoOffsetHours(4),
      app: 'Figma',
      title: 'Brand Refresh — Hero',
      site: 'figma.com',
      url: null,
      screenshotId: 'scr-demo-002',
      employee: { id: 'emp-marcus-rivera', name: 'Marcus Rivera' },
      project: ALPHA,
    },
    {
      id: 'evt-act-004',
      occurredAt: isoOffsetHours(5),
      app: 'Figma',
      title: 'Brand Refresh — Hero',
      site: 'figma.com',
      url: null,
      screenshotId: null,
      employee: { id: 'emp-marcus-rivera', name: 'Marcus Rivera' },
      project: ALPHA,
    },
    {
      id: 'evt-act-005',
      occurredAt: isoOffsetHours(7),
      app: 'Figma',
      title: 'Brand Refresh — Hero',
      site: 'figma.com',
      url: null,
      screenshotId: 'scr-demo-003',
      employee: { id: 'emp-sarah-chen', name: 'Sarah Chen' },
      project: ALPHA,
    },
    {
      id: 'evt-act-006',
      occurredAt: isoOffsetHours(8),
      app: 'Figma',
      title: 'Brand Refresh — Hero',
      site: null,
      url: 'https://figma.com/file/abc123/Brand-Refresh',
      screenshotId: null,
      employee: { id: 'emp-anita-patel', name: 'Anita Patel' },
      project: ALPHA,
    },
    {
      id: 'evt-act-007',
      occurredAt: isoOffsetHours(11),
      app: 'Figma',
      title: 'Brand Refresh — Hero',
      site: 'figma.com',
      url: null,
      screenshotId: 'scr-demo-004',
      employee: { id: 'emp-anita-patel', name: 'Anita Patel' },
      project: ALPHA,
    },
    {
      id: 'evt-act-008',
      occurredAt: isoOffsetHours(13),
      app: 'Figma',
      title: 'Brand Refresh — Hero',
      site: 'figma.com',
      url: null,
      screenshotId: null,
      employee: { id: 'emp-james-obrien', name: 'James O’Brien' },
      project: ALPHA,
    },
  ],
  totalEvents: 8,
};
